package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.models.CancelClaimRequest
import com.sddamico.models.PlayerClaimResponse
import com.sddamico.models.ResolveClaimRequest
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.LineHeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private val claimAmber = Color.rgb(0xD97706)
private val claimAmberBg = Color.rgb(0x78350F)

@Composable
fun ClaimCard(
    claim: PlayerClaimResponse,
    isOwnRoster: Boolean,
    onRefresh: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var cancelling by remember { mutableStateOf(false) }

    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderLeft(3.px, LineStyle.Solid, claimAmber)
            .borderRadius(8.px)
            .padding(1.cssRem)
            .toAttrs()
    ) {
        Column(Modifier.gap(0.375.cssRem)) {
            Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                Div(
                    Modifier
                        .backgroundColor(claimAmber)
                        .borderRadius(4.px)
                        .padding(topBottom = 0.125.cssRem, leftRight = 0.5.cssRem)
                        .display(DisplayStyle.Flex)
                        .alignItems(AlignItems.Center)
                        .toAttrs()
                ) {
                    SpanText(
                        if (claim.claimType == "WAIVER") "Pending Waiver Claim" else "Pending Claim",
                        Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold).color(Colors.White)
                            .lineHeight(LineHeight.Normal)
                    )
                }
                SpanText(
                    claim.claimedByName,
                    Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary)
                )
            }

            Row(Modifier.gap(0.375.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SportBadge(claim.addedPlayerSport, isLiv = claim.rosterSlot == "LIV_LINE")
                SpanText(
                    "+${claim.addedPlayerName}",
                    Modifier.fontSize(0.875.cssRem).color(Color("#16a34a"))
                )
                if (claim.droppedPlayerName != null) {
                    SpanText(
                        "-${claim.droppedPlayerName}",
                        Modifier.fontSize(0.875.cssRem).color(Color("#dc2626"))
                    )
                }
            }

            Row(Modifier.gap(0.75.cssRem)) {
                SpanText(
                    formatSlotName(claim.rosterSlot),
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
                )
                SpanText(
                    "Submitted: ${claim.createdAt.substringBefore("T")}",
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
                )
            }

            if (claim.waiverEndDate != null) {
                SpanText(
                    "Waivers process: ${formatWaiverEndDate(claim.waiverEndDate)}",
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
                )
            }

            if (claim.notes != null) {
                SpanText(
                    claim.notes,
                    Modifier.fontSize(0.8125.cssRem).fontStyle(FontStyle.Italic).color(SitePalette.textSecondary)
                )
            }

            if (isOwnRoster) {
                Row(Modifier.margin(top = 0.375.cssRem).fillMaxWidth().position(Position.Relative)) {
                    ConfirmableButton(
                        label = "Cancel Claim",
                        loadingLabel = "Cancelling...",
                        confirmLabel = "Are you sure?",
                        isLoading = cancelling,
                        buttonModifier = Modifier
                            .backgroundColor(if (cancelling) SitePalette.border else SitePalette.error)
                            .color(Colors.White)
                            .borderRadius(6.px)
                            .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                            .fontSize(0.75.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .transition(Transition.of("background-color", 150.ms)),
                        onConfirm = {
                            scope.launch {
                                cancelling = true
                                try {
                                    val body = apiJson.encodeToString(CancelClaimRequest(claim.id))
                                    apiPost("/api/claims/cancel", body, PlayerClaimResponse.serializer())
                                    onRefresh()
                                } catch (_: Exception) {}
                                cancelling = false
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
fun PendingClaimCard(
    claim: PlayerClaimResponse,
    onRefresh: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var processing by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderLeft(3.px, LineStyle.Solid, claimAmber)
            .borderRadius(8.px)
            .padding(1.cssRem)
            .toAttrs()
    ) {
        Column(Modifier.gap(0.375.cssRem)) {
            Row(Modifier.gap(0.5.cssRem).fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Div(
                    Modifier
                        .backgroundColor(claimAmber)
                        .borderRadius(4.px)
                        .padding(topBottom = 0.125.cssRem, leftRight = 0.5.cssRem)
                        .display(DisplayStyle.Flex)
                        .alignItems(AlignItems.Center)
                        .toAttrs()
                ) {
                    SpanText(
                        if (claim.claimType == "WAIVER") "Pending Waiver Claim" else "Pending Claim",
                        Modifier.fontSize(0.6875.cssRem).fontWeight(FontWeight.SemiBold).color(Colors.White)
                            .lineHeight(LineHeight.Normal)
                    )
                }
                SpanText(
                    claim.clubName,
                    Modifier.fontSize(0.9375.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text)
                )
                Spacer()
                SpanText(
                    claim.claimedByName,
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.textSecondary)
                )
            }

            Row(Modifier.gap(0.375.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SportBadge(claim.addedPlayerSport, isLiv = claim.rosterSlot == "LIV_LINE")
                SpanText(
                    "+${claim.addedPlayerName}",
                    Modifier.fontSize(0.875.cssRem).color(Color("#16a34a"))
                )
                if (claim.droppedPlayerName != null) {
                    SpanText(
                        "-${claim.droppedPlayerName}",
                        Modifier.fontSize(0.875.cssRem).color(Color("#dc2626"))
                    )
                }
            }

            Row(Modifier.gap(0.75.cssRem)) {
                SpanText(
                    formatSlotName(claim.rosterSlot),
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
                )
                SpanText(
                    "Submitted: ${claim.createdAt.substringBefore("T")}",
                    Modifier.fontSize(0.75.cssRem).color(SitePalette.textMuted)
                )
            }

            if (claim.notes != null) {
                SpanText(
                    claim.notes,
                    Modifier.fontSize(0.8125.cssRem).fontStyle(FontStyle.Italic).color(SitePalette.textSecondary)
                )
            }

            if (claim.duplicateClaimCount > 0) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(Color("#3D2A0A"))
                        .border(1.px, LineStyle.Solid, Color("#8B5E03"))
                        .borderRadius(6.px)
                        .padding(0.5.cssRem)
                        .toAttrs()
                ) {
                    SpanText(
                        "${claim.duplicateClaimCount} other team(s) also claiming this player",
                        Modifier.fontSize(0.75.cssRem).fontWeight(FontWeight.Medium).color(claimAmber)
                    )
                }
            }

            if (errorMessage != null) {
                SpanText(errorMessage!!, Modifier.fontSize(0.75.cssRem).color(SitePalette.error))
            }

            // Approve / Deny buttons
            Row(Modifier.margin(top = 0.375.cssRem).gap(0.5.cssRem).fillMaxWidth().position(Position.Relative)) {
                ConfirmableButton(
                    label = "Approve",
                    loadingLabel = "Processing...",
                    confirmLabel = "Are you sure?",
                    isLoading = processing,
                    buttonModifier = Modifier
                        .backgroundColor(if (processing) SitePalette.border else SitePalette.primary)
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .transition(Transition.of("background-color", 150.ms)),
                    onConfirm = {
                        scope.launch {
                            processing = true
                            errorMessage = null
                            try {
                                val body = apiJson.encodeToString(ResolveClaimRequest(claim.id, "APPROVE"))
                                apiPost("/api/claims/resolve", body, PlayerClaimResponse.serializer())
                                onRefresh()
                            } catch (e: ApiException) {
                                errorMessage = e.errorMessage
                            } catch (_: Exception) {
                                errorMessage = "Failed to approve claim"
                            }
                            processing = false
                        }
                    },
                )

                ConfirmableButton(
                    label = "Deny",
                    loadingLabel = "Processing...",
                    confirmLabel = "Are you sure?",
                    isLoading = processing,
                    buttonModifier = Modifier
                        .backgroundColor(if (processing) SitePalette.border else SitePalette.error)
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .transition(Transition.of("background-color", 150.ms)),
                    onConfirm = {
                        scope.launch {
                            processing = true
                            errorMessage = null
                            try {
                                val body = apiJson.encodeToString(ResolveClaimRequest(claim.id, "DENY"))
                                apiPost("/api/claims/resolve", body, PlayerClaimResponse.serializer())
                                onRefresh()
                            } catch (e: ApiException) {
                                errorMessage = e.errorMessage
                            } catch (_: Exception) {
                                errorMessage = "Failed to deny claim"
                            }
                            processing = false
                        }
                    },
                )
            }
        }
    }
}

private fun formatSlotName(slot: String): String = when (slot) {
    "GOLFER_1" -> "Golfer 1"
    "GOLFER_2" -> "Golfer 2"
    "GOLFER_3" -> "Golfer 3"
    "GOLFER_4" -> "Golfer 4"
    "GOLFER_5" -> "Golfer 5"
    "LIV_LINE" -> "LIV Line"
    "MENS_TENNIS_1" -> "Men's Tennis 1"
    "MENS_TENNIS_2" -> "Men's Tennis 2"
    "MENS_TENNIS_3" -> "Men's Tennis 3"
    "MENS_TENNIS_4" -> "Men's Tennis 4"
    "WOMENS_TENNIS_1" -> "Women's Tennis 1"
    "WOMENS_TENNIS_2" -> "Women's Tennis 2"
    else -> slot
}
